class KleryaBestsellers extends HTMLElement {
  constructor() {
    super();
    this.grid = null;
    this.tabButtons = [];
    this.viewLink = null;
    this.lastFocusedTrigger = null;
    this.soldOutLabel = '';
    this._onEscapeBound = this.onEscape.bind(this);
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-klerya-bestsellers-grid]');
    if (!this.grid) return;

    this.soldOutLabel = this.getAttribute('data-sold-out-label') || '';
    this.tabButtons = Array.from(this.querySelectorAll('.klerya-bestsellers__tab[data-klerya-tab]'));
    this.viewLink = this.querySelector('[data-klerya-tab-view-link]');

    this.tabButtons.forEach((btn) => btn.addEventListener('click', this.onTabClick.bind(this)));
    this.addEventListener('click', this.onClick.bind(this));
    this.grid.addEventListener('click', this.onSwatchClick.bind(this));
    this.grid.addEventListener('mouseover', this.onSwatchHover.bind(this));
    this.grid.addEventListener('mouseout', this.onSwatchUnhover.bind(this));
    this.grid.addEventListener('focusin', this.onSwatchHover.bind(this));
    this.grid.addEventListener('focusout', this.onSwatchUnhover.bind(this));
    document.addEventListener('keydown', this._onEscapeBound);

    const firstActive = this.tabButtons.find((btn) => btn.getAttribute('aria-selected') === 'true') || this.tabButtons[0];
    if (firstActive) this.filterByTab(firstActive.dataset.kleryaTab);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onEscapeBound);
  }

  filterByTab(tabNum) {
    const items = this.grid.querySelectorAll('.klerya-bestsellers-card[data-klerya-tab]');
    let visibleCount = 0;
    items.forEach((item) => {
      const show = item.dataset.kleryaTab === tabNum;
      item.hidden = !show;
      if (show) visibleCount += 1;
    });
    const cols = Math.max(1, Math.min(visibleCount, 4));
    this.grid.style.setProperty('--klerya-visible-cols', String(cols));
  }

  onTabClick(event) {
    const btn = event.currentTarget;
    this.tabButtons.forEach((b) => b.setAttribute('aria-selected', 'false'));
    btn.setAttribute('aria-selected', 'true');
    this.filterByTab(btn.dataset.kleryaTab);

    if (this.viewLink) {
      const href = btn.getAttribute('data-klerya-tab-link') || '';
      if (href) {
        this.viewLink.hidden = false;
        this.viewLink.setAttribute('href', href);
      } else {
        this.viewLink.hidden = true;
        this.viewLink.removeAttribute('href');
      }
    }

    if (this.grid.scrollTo) this.grid.scrollTo({ left: 0, behavior: 'smooth' });
  }

  openModal(id, trigger) {
    const modal = id ? document.getElementById(id) : null;
    if (!modal) return;
    this.lastFocusedTrigger = trigger || document.activeElement;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('overflow-hidden');
    const focusTarget = modal.querySelector('input, button, a, [tabindex]:not([tabindex="-1"])');
    if (typeof trapFocus === 'function') {
      trapFocus(modal, focusTarget || modal);
    } else if (focusTarget) {
      focusTarget.focus();
    }
  }

  closeModal(modal) {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.lastFocusedTrigger);
    } else if (this.lastFocusedTrigger && this.lastFocusedTrigger.focus) {
      this.lastFocusedTrigger.focus();
    }
    this.lastFocusedTrigger = null;
  }

  onClick(event) {
    const actionTarget = event.target.closest('[data-action]');
    if (!actionTarget || !this.contains(actionTarget)) return;
    const action = actionTarget.getAttribute('data-action');

    if (action === 'open-modal') {
      event.preventDefault();
      this.openModal(actionTarget.getAttribute('aria-controls'), actionTarget);
    } else if (action === 'close-modal') {
      event.preventDefault();
      this.closeModal(actionTarget.closest('.klerya-modal'));
    } else if (action === 'quick-add-to-cart') {
      event.preventDefault();
      this.quickAdd(actionTarget);
    } else if (action === 'open-quick-shop') {
      const card = actionTarget.closest('.klerya-bestsellers-card');
      const link = card && card.querySelector('[data-klerya-product-link]');
      if (link) {
        event.preventDefault();
        window.location.href = link.href;
      }
    }
  }

  onEscape(event) {
    if (event.key !== 'Escape') return;
    this.closeModal(this.querySelector('.klerya-modal[aria-hidden="false"]'));
  }

  refreshCartFromResponse(response, activeElement) {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cart || !response) {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      return;
    }
    if (cart.setActiveElement && activeElement) cart.setActiveElement(activeElement);
    if (cart.renderContents) cart.renderContents(response);
    if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
  }

  quickAdd(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (cart && cart.getSectionsToRender) {
      formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
      formData.append('sections_url', window.location.pathname);
      if (cart.setActiveElement) cart.setActiveElement(button);
    }

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/javascript' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', config)
      .then((response) => response.json())
      .then((response) => {
        if (response && response.status) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'klerya-bestsellers',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          throw new Error(response.description || response.message || 'Cart error');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'klerya-bestsellers',
            productVariantId: variantId,
            cartData: response,
          });
        }
        this.refreshCartFromResponse(response, button);
      })
      .catch((error) => {
        const card = button.closest('.klerya-bestsellers-card');
        const status = card && card.querySelector('[data-klerya-variant-status]');
        if (status) status.textContent = error.message;
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      });
  }

  onSwatchHover(event) {
    const swatch = event.target.closest('[data-klerya-variant-id]');
    if (!swatch) return;
    const card = swatch.closest('.klerya-bestsellers-card');
    if (!card) return;
    const title = card.querySelector('[data-klerya-variant-title]');
    if (title) title.textContent = swatch.getAttribute('data-klerya-variant-title') || '';
  }

  onSwatchUnhover(event) {
    const swatch = event.target.closest('[data-klerya-variant-id]');
    if (!swatch) return;
    const related = event.relatedTarget;
    if (related && related.closest && related.closest('[data-klerya-variant-id]') === swatch) return;
    const card = swatch.closest('.klerya-bestsellers-card');
    if (!card) return;
    const title = card.querySelector('[data-klerya-variant-title]');
    const selected = card.querySelector('[data-klerya-variant-id].is-selected');
    if (title && selected) title.textContent = selected.getAttribute('data-klerya-variant-title') || '';
  }

  onSwatchClick(event) {
    const swatch = event.target.closest('[data-klerya-variant-id]');
    if (!swatch) return;
    event.preventDefault();

    const card = swatch.closest('.klerya-bestsellers-card');
    if (!card) return;

    const actionButton = card.querySelector('.klerya-bestsellers-card__quick-add[data-klerya-available-action]');
    const title = card.querySelector('[data-klerya-variant-title]');
    const price = card.querySelector('[data-klerya-variant-price]');
    const image = card.querySelector('[data-klerya-product-image]');
    const status = card.querySelector('[data-klerya-variant-status]');
    const variantUrl = swatch.getAttribute('data-klerya-variant-url') || '';

    card.querySelectorAll('[data-klerya-variant-id]').forEach((button) => {
      const selected = button === swatch;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    if (title) title.textContent = swatch.getAttribute('data-klerya-variant-title') || '';

    if (price) {
      const variantPrice = swatch.getAttribute('data-klerya-variant-price') || '';
      const comparePrice = swatch.getAttribute('data-klerya-variant-compare') || '';
      price.innerHTML = comparePrice
        ? `<span>${this.escHtml(variantPrice)}</span><br><s>${this.escHtml(comparePrice)}</s>`
        : `<span>${this.escHtml(variantPrice)}</span>`;
    }

    if (image && swatch.getAttribute('data-klerya-variant-image')) {
      image.setAttribute('src', swatch.getAttribute('data-klerya-variant-image'));
      if (swatch.getAttribute('data-klerya-variant-srcset')) {
        image.setAttribute('srcset', swatch.getAttribute('data-klerya-variant-srcset'));
      }
    }

    if (variantUrl) {
      card.querySelectorAll('[data-klerya-product-link]').forEach((link) => link.setAttribute('href', variantUrl));
    }

    if (actionButton) {
      const availableAction = actionButton.getAttribute('data-klerya-available-action') || 'quick-add-to-cart';
      this.setActionButtonState(actionButton, swatch, availableAction);
    }

    if (status) {
      status.textContent = swatch.getAttribute('data-klerya-variant-available') === 'true' ? '' : this.soldOutLabel;
    }

  }

  setActionButtonState(button, swatch, availableAction) {
    const isAvailable = swatch.getAttribute('data-klerya-variant-available') === 'true';
    const card = button.closest('.klerya-bestsellers-card');
    const productTitle = card && card.querySelector('.klerya-bestsellers-card__name')
      ? card.querySelector('.klerya-bestsellers-card__name').textContent.trim()
      : '';
    button.setAttribute('data-variant-id', swatch.getAttribute('data-klerya-variant-id'));

    if (isAvailable) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.setAttribute('data-action', availableAction);
      button.textContent = button.getAttribute('data-klerya-add-label') || button.textContent;
    } else {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.removeAttribute('data-action');
      button.textContent = this.soldOutLabel;
    }
    if (productTitle) button.setAttribute('aria-label', `${button.textContent}: ${productTitle}`);
  }

  escHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

}

if (!customElements.get('klerya-bestsellers')) {
  customElements.define('klerya-bestsellers', KleryaBestsellers);
}
